import React, { useEffect, useState } from "react";
import { Card, List, notification, Popconfirm, Space, Tooltip } from "antd";
import { ListItem, ListItemAvatar, Typography } from "@mui/material";
import { useGetOrgIdentifier } from "../../../generic/hooks/useGetOrgIdentifier";
import apiService from "../../../../service/apiservice/apiService";
import SecondaryButton from "../../../../components/generic/buttons/SecondaryButton";
import PrimaryButton from "../../../../components/generic/buttons/PrimaryButton";
import SmartIcon from "../../../../components/generic/genericIcon/SmartIcon";
import NoDataMessageComponent from "../../../../components/generic/nodatamessage/NoDataMessageComponent";
import CustomModal from "../../../../components/generic/custommodal/CustomModal";
import AddEditDocumentsForm from "./AddEditDocumentsForm";
import ConfigurationConstants from "../../../utils/configurationconstants";
import { useRecoilValue } from "recoil";
import { currentConfiguration } from "../../../../service/memory/recoil/configurationstore";
import { getMediaKeyObjectStore } from "../../../../components/generic/formbuilder/networkservice/FormService";
import base64 from "base-64";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { getFullName } from "../../../careprovider/visits/utils";
import "../../../organizationsettings/prferences/preferences.css"

const DocumentsSettings = ({ userDetails, workforceIdentifier }) => {
  const organizationIdentifier = useGetOrgIdentifier();
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isModalVisible, setModalVisible] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [documentDetails, setDocumentDetails] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [formAction, setFormAction] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const configuration = useRecoilValue(currentConfiguration);
  const bucketName =
    configuration[ConfigurationConstants.NIRAMAYA_FORMS_BUCKETNAME];

  const handleClick = async (e) => {
    e.preventDefault();
    setErrors(validate(documentDetails));
    setIsSubmit(true);
  };

  const validate = (values) => {
    const errors = {};
    if (!values?.title) {
      errors.title = "Title is required!";
    }
    if (!values?.fileName) {
      errors.fileName = "Please upload a document!";
    }
    return errors;
  };

  const handleExport = async () => {
    setExportLoading(true);
    const zip = new JSZip();

    // Loop over your list of docs
    for (const document of documents) {
      if (!document?.contentId) continue;

      // Fetch Base64 from your function
      const base64String = await getMediaKeyObjectStore(
        document?.contentId,
        bucketName?.value
      );

      // Convert Base64 → Uint8Array (same as your viewClick logic)
      const byteCharacters = atob(base64String);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      // Add file into ZIP
      // document.fileName should include extension (e.g. report.pdf)
      zip.file(document.fileName, byteArray, { binary: true });
    }

    // Generate ZIP and trigger download
    const content = await zip.generateAsync({ type: "blob" });
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .replace("T", "_")
      .slice(0, 19);

    const fileName =
      getFullName(userDetails?.givenName, userDetails?.familyName) || documents;
    saveAs(content, `${fileName}_${timestamp}.zip`);
    setExportLoading(false);
  };

  const addClick = () => {
    setDocumentDetails(null);
    setFormAction("add");
    setModalVisible(true);
  };

  const editClick = async (id) => {
    const response = await apiService.get(
      `niramayafoundation/api/v1/documents/${id}`
    );
    setDocumentDetails(response);
    setFormAction("edit");
    setModalVisible(true);
  };

  const getDocuments = async () => {
    setIsLoading(true);
    const endpoint = "niramayafoundation/api/v1/documents";
    const params = {
      workforceIdentifier,
    };
    const response = await apiService.get(endpoint, params);
    setDocuments(response);
    setIsLoading(false);
  };

  const viewClick = async (document) => {
    if (!document?.contentId) return;

    const base64String = await getMediaKeyObjectStore(
      document?.contentId,
      bucketName?.value
    );
    // Convert Base64 to a Blob
    const byteCharacters = atob(base64String);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: document?.fileType });

    // Create object URL and open
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl);
  };

  const handleDelete = async (id) => {
    setDeleteLoading(true);
    const endpoint = "niramayafoundation/api/v1/documents";
    const param = {
      organizationIdentifier,
      id,
    };
    const response = await apiService.delete(
      endpoint,
      param,
      "Document deleted succesfully",
      "Could not delete document"
    );
    if (response?.message) {
      getDocuments();
    }
    setDeleteLoading(false);
  };

  const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result.split(",")[1]); // strip prefix
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  };

  const handleDocumentUpload = async () => {
    if (!documentDetails?.file) return null;

    try {
      const base64Data = await readFileAsBase64(documentDetails.file);

      const response = await apiService.post(
        `event/api/v1/store/object/json/upload?bucketName=${bucketName?.value}`,
        {
          object: base64.encode(base64Data),
          extension: documentDetails.file.name.split(".").pop(),
        }
      );

      return response?.objectId || null;
    } catch (error) {
      console.error("Upload failed:", error);
      return null;
    }
  };

  const handleSubmit = async () => {
    setSubmitLoading(true);

    // Only upload if user has chosen a new file
    let objectId = documentDetails?.contentId || null;
    if (documentDetails?.file) {
      objectId = await handleDocumentUpload();
      if (!objectId) {
        notification.error({
          message: "Couldn’t upload the document. Please try again.",
          placement: "bottomRight",
        });
        setSubmitLoading(false);
        return;
      }
    }

    const payload = {
      organizationIdentifier,
      workforceIdentifier,
      title: documentDetails?.title,
      description: documentDetails?.description,
      contentId: objectId,
      fileName: documentDetails?.fileName,
      fileType: documentDetails?.fileType,
    };
    const url =
      formAction === "edit"
        ? `niramayafoundation/api/v1/documents/${documentDetails?.id}`
        : "niramayafoundation/api/v1/documents";
    const method = formAction === "edit" ? "put" : "post";
    const successMessage =
      formAction === "edit"
        ? "Document updated successfully"
        : "Added Document successfully";
    const errorMessage =
      formAction === "edit"
        ? "Error while updating document"
        : "Error while adding document";

    const response = await apiService[method](
      url,
      payload,
      successMessage,
      errorMessage
    );

    if (response?.id) {
      setIsSubmit(false);
      setModalVisible(false);
      getDocuments();
      setDocumentDetails(null);
    }
    setSubmitLoading(false);
  };

  const handleCancel = () => {
    setIsSubmit(false);
    setModalVisible(false);
    setDocumentDetails(null);
    setErrors({});
  };

  useEffect(() => {
    if (Object.keys(errors).length === 0 && isSubmit) {
      handleSubmit();
    }
  }, [errors, isSubmit]);

  useEffect(() => {
    getDocuments();
  }, [organizationIdentifier]);

  const customModelButtons = (
    <>
      <SecondaryButton buttonText={"Cancel"} handleOnClick={handleCancel} />
      <PrimaryButton
        buttonText={formAction === "edit" ? "Edit" : "Add"}
        handleOnClick={handleClick}
        loading={submitLoading}
      />
    </>
  );

  return (
    <>
      <Card
        className="services-card"
        title={
          <>
            <div className="preference-card-title">
              <strong className="services-card-title-font">Documents</strong>
              <Space>
                <PrimaryButton
                  buttonText="Add Document"
                  handleOnClick={addClick}
                />
                <PrimaryButton
                  buttonText="Export"
                  handleOnClick={handleExport}
                  loading={exportLoading}
                  buttonDisable={!documents?.length}
                />
              </Space>
            </div>
          </>
        }
      >
        {documents && documents?.length ? (
          <List
            loading={isLoading}
            dataSource={documents}
            renderItem={(document) => (
              <ListItem className="services-listitem" key={document?.id}>
                <List.Item.Meta
                  style={{ cursor: "default" }}
                  title={
                    <Typography className="services-listitem-font">
                      {document?.title}
                    </Typography>
                  }
                  description={document?.description}
                />

                <ListItemAvatar className="services-list-item-action">
                  <Space size="middle">
                    <Tooltip title="View Document" placement="bottom">
                      <>
                        <SmartIcon
                          iconKey="report"
                          width="20"
                          height="20"
                          handleIconClick={() => viewClick(document)}
                        />
                      </>
                    </Tooltip>
                    <Tooltip title="Edit" placement="bottom">
                      <>
                        <SmartIcon
                          iconKey="edit"
                          width="20"
                          height="20"
                          handleIconClick={() => editClick(document?.id)}
                        />
                      </>
                    </Tooltip>
                    <Tooltip title="Delete" placement="bottom">
                      <>
                        <Popconfirm
                          title="Are you sure to delete this Document?"
                          placement="left"
                          onConfirm={() => handleDelete(document?.id)}
                          okText="Confirm"
                          cancelText="Cancel"
                          overlayClassName="article-delete-popconfirm"
                          okButtonProps={{ loading: deleteLoading }}
                        >
                          <>
                            <SmartIcon
                              iconKey="delete"
                              width="20"
                              height="20"
                            />
                          </>
                        </Popconfirm>
                      </>
                    </Tooltip>
                  </Space>
                </ListItemAvatar>
              </ListItem>
            )}
          />
        ) : (
          <NoDataMessageComponent text="No data available" />
        )}
      </Card>

      {isModalVisible && (
        <CustomModal
          closable={false}
          isModalOpen={isModalVisible}
          footer={[customModelButtons]}
          closeModal={setModalVisible}
          title={
            formAction === "edit" ? "Edit Document Details" : "Add Document"
          }
        >
          <AddEditDocumentsForm
            documentDetails={documentDetails}
            setDocumentDetails={setDocumentDetails}
            errors={errors}
            onUploadSuccess={handleSubmit}
          />
        </CustomModal>
      )}
    </>
  );
};

export default DocumentsSettings;
